import ky from 'ky';

import type { UserType } from '~settings/api/lib/schemas';

interface Permissions {
  [labelID: string]: string[];
}

export interface UserResponse {
  permissions: Permissions;
  name: string;
  email: string;
}

interface UserInfoResponse {
  email: string;
  firstName: string;
  lastName: string;
  userLocale: string;
  userFeatures: UserFeature[];
  isInternal: boolean;
  userId: string;
  userType?: UserType;
}

export interface UserFeature {
  enabled: boolean;
  entity: {
    level: string;
    humanReadableName: string;
  };
  humanReadableName: string;
  systemPermission: string;
}

interface RequestOptions {
  retryCount: number;
  retryDelay: number;
}

const client = ky.create({
  prefixUrl: '/api',
  credentials: 'include',
  timeout: 1000 * 30 * 3,
  retry: 0,
});

const defaultRequestOptions: RequestOptions = {
  retryCount: 2,
  retryDelay: 1000,
};

async function fetchUserPermissions() {
  try {
    return await client.get<UserResponse>('structured_permissions').json();
  } catch {
    throw new Error('Failed to fetch user permissions');
  }
}

const bff = {
  async authenticate(token: string, options: Partial<RequestOptions> = {}) {
    try {
      return await client
        .post('auth', {
          credentials: undefined,
          headers: { 'Content-Type': 'application/json' },
          json: { token },
          retry: {
            limit: options.retryCount || defaultRequestOptions.retryCount,
            methods: ['post'],
            statusCodes: [401, 403, 500],
            delay: () => options.retryDelay || defaultRequestOptions.retryDelay,
          },
          hooks: {
            beforeRetry: [
              ({ error, retryCount }) => {
                console.error(
                  `Attempt ${retryCount}: Failed to authenticate`,
                  error,
                );
              },
            ],
          },
        })
        .text();
    } catch (error) {
      console.error('Error during authentication', error);

      throw new Error('Failed to authenticate.');
    }
  },
  async refreshAuthToken() {
    try {
      return await client
        .post('refresh_auth', {
          credentials: 'include',
        })
        .text();
    } catch {
      throw new Error('Failed to refresh auth through bff');
    }
  },
  async logout() {
    try {
      await client.post('logout');
    } catch {
      throw new Error('Failed to delete session data and cookies through bff');
    }
  },
  async getUser(token?: string) {
    try {
      return await fetchUserPermissions();
    } catch {
      if (token) {
        try {
          await bff.authenticate(token);

          return await fetchUserPermissions();
        } catch (authError) {
          console.error('Error during re-authentication:', authError);
        }
      }

      throw new Error('Failed to fetch user permissions');
    }
  },
  async getUserInfo(token?: string, id?: string) {
    try {
      return await client
        .get<UserInfoResponse>(`user-info/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'x-no-gzip-response': 'true',
          },
        })
        .json();
    } catch {
      throw new Error('Failed to fetch user info');
    }
  },
};

export { bff };
